var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, isValidElement } from "react";
import { StyledTableCell, StyledTableCellContent, StyledTableCellContentGlobalFilter, StyledTableCellContentWithAction, StyledTableCellContentWithActionInner, } from "./TableCell.style";
import { cellActions } from "./utils";
import { TableKebabActionMenu, renderTableActionAsActionItems } from "./KebabWithPortal";
import { StyledKebabPlaceholder } from "./KebabWithPortal.style";
import { isGroupParent } from "./TableGroupingColumn";
import TableCellContent, { valueToString } from "./TableCellContent";
import { isTableActionColumnRenderer, isTableActionColumnType } from "./TableActionColumn";
import { isTableFillerType } from "./TableFillerColumn";
import flattenChildren from "../../utils/flattenChildren";
import { withContextMenu } from "../menu/ContextActionMenu";
import useIdAttrs from "../../hooks/useIdAttrs";
import get from "lodash/get";
const useFormatterParams = ({ id, column, data, onDataChange, variant, globalFilters, insightsAttr, shouldFitContent, actionMenuMaxHeight, actionMenuHandleQuickFind, tableActionColumns, ariaColumnIndex, columnIndex, columnsCount, dataIndex, isSelected, dataItem, }) => ({
    id,
    column,
    data,
    onDataChange,
    variant,
    globalFilters,
    insightsAttr,
    shouldFitContent,
    actionMenuMaxHeight,
    actionMenuHandleQuickFind,
    tableActionColumns,
    // removed kendo params are 'undefined'
    onContextMenu: undefined,
    locked: undefined,
    colSpan: undefined,
    dataItem,
    field: column.field || column.title,
    editor: undefined,
    format: undefined,
    className: undefined,
    render: undefined,
    onChange: undefined,
    selectionChange: undefined,
    columnIndex,
    columnsCount,
    rowType: undefined,
    level: undefined,
    expanded: undefined,
    dataIndex,
    style: undefined,
    ariaColumnIndex,
    isSelected,
});
const useTableCell = ({ id, insightsAttr, shouldFitContent, columnIndex, colSpan, dataIndex, column, column: { align, type }, }) => (Object.assign(Object.assign({ textAlign: align }, (isTableFillerType(type)
    ? {
        "aria-hidden": true,
    }
    : Object.assign(Object.assign({ role: "gridcell" }, (columnIndex !== undefined && { "aria-colindex": columnIndex + 1 })), { "aria-rowindex": dataIndex + 1 }))), { "aria-selected": false, colSpan, $shouldFitContent: Boolean(shouldFitContent), id, "data-insights": insightsAttr, $type: type, locked: column.locked }));
const TableCell = (props) => {
    var _a;
    const { id, column, variant, globalFilters, insightsAttr, actionMenuMaxHeight, actionMenuHandleQuickFind, tableActionColumns, dataIndex, dataItem, } = props;
    const formatterParams = useFormatterParams(props);
    const { valueFormatter, cellRenderer, editMode, actionMenuRenderer, hasActions = () => true, align, whiteSpace, columnHeaderMenuRenderer, tooltip, type, field, } = column;
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const tableCellProps = useTableCell(props);
    const value = field ? get(dataItem, field) : undefined;
    const isInGlobalFilters = !!(globalFilters === null || globalFilters === void 0 ? void 0 : globalFilters.find((filter) => { var _a; return filter.key === column.field && ((_a = filter.values) === null || _a === void 0 ? void 0 : _a.includes(`${value}`)); }));
    const valueFormatted = valueFormatter ? valueFormatter(Object.assign({ value }, formatterParams)) : value;
    const tooltipMessage = tooltip === ""
        ? undefined
        : tooltip ||
            valueToString(!cellRenderer && !editMode && whiteSpace === "nowrap" ? valueFormatted : null) ||
            undefined;
    const cellRendererParams = Object.assign(Object.assign({ value, valueFormatted }, formatterParams), { originalContent: React.createElement(React.Fragment, null) });
    const content = React.createElement(TableCellContent, Object.assign({}, cellRendererParams));
    const displayCellActionMenu = (hasActions === null || hasActions === void 0 ? void 0 : hasActions(cellRendererParams)) && actionMenuRenderer;
    const hasContextMenu = tableActionColumns && (tableActionColumns === null || tableActionColumns === void 0 ? void 0 : tableActionColumns.length) > 0;
    const actionColumnQuickFindHandler = (_a = tableActionColumns === null || tableActionColumns === void 0 ? void 0 : tableActionColumns.map((a) => a.props.actionMenuHandleQuickFind).filter(Boolean)) === null || _a === void 0 ? void 0 : _a[0];
    const tableActionColumnProps = (tableActionColumns
        ? tableActionColumns
            .flatMap((actionColumn) => {
            if (actionColumn.props.children === undefined) {
                return [];
            }
            const actionsChildrenArray = isTableActionColumnRenderer(actionColumn.props.children)
                ? []
                : Children.toArray(actionColumn.props.children);
            return [
                {},
                actionsChildrenArray
                    .map((actionChild) => isValidElement(actionChild) ? actionChild.props : undefined)
                    .filter(Boolean),
            ];
        })
            .slice(1)
        : []).flat();
    const isGroupParentCell = isGroupParent(dataItem);
    const getKebabPlaceholderBefore = () => (align === "center" ? React.createElement(StyledKebabPlaceholder, null) : null);
    const getKebabPlaceholderAfter = () => (columnHeaderMenuRenderer && align !== "left") || (actionMenuRenderer && align === "right") ? (React.createElement(StyledKebabPlaceholder, null)) : null;
    // joins cell actions and action column actions into one context menu
    const contextMenuRenderer = () => __awaiter(void 0, void 0, void 0, function* () {
        return Promise.all([
            // resolve action columns actions
            ...(tableActionColumns || []).map((actionColumn) => {
                return Promise.resolve(
                // actionMenuRenderer can be passed to action columns as a prop
                actionColumn.props.actionMenuRenderer
                    ? actionColumn.props.actionMenuRenderer(cellRendererParams)
                    : // or it can be passed as a children renderer
                        isTableActionColumnRenderer(actionColumn.props.children)
                            ? Promise.resolve(actionColumn.props.children(cellRendererParams)).then(flattenChildren)
                            : []);
            }),
            // resolve cell actions (but ignore action column cells)
            Promise.resolve(displayCellActionMenu && !isTableActionColumnType(type) ? actionMenuRenderer(cellRendererParams) : []),
        ]).then(([actionColumnMenu, cellMenu]) => {
            const isCellActions = Array.isArray(cellMenu) && cellMenu.length > 0;
            // construct context menu
            const contextMenuActions = [
                ...(isCellActions ? cellActions(cellMenu) : []),
                // add separator if has both cell and row actions
                ...(isCellActions && (tableActionColumnProps === null || tableActionColumnProps === void 0 ? void 0 : tableActionColumnProps.length) > 0 ? [{}] : []),
                // columns actions as children
                ...tableActionColumnProps,
                // column actions with action renderer
                ...(actionColumnMenu ? cellActions(actionColumnMenu) : []),
            ];
            return renderTableActionAsActionItems(contextMenuActions, cellRendererParams, insightsAttr && `${insightsAttr}_context_menu`);
        });
    });
    const cell = (React.createElement(React.Fragment, null, displayCellActionMenu ? (React.createElement(StyledTableCellContentWithAction, { isGroupParentCell: isGroupParentCell, isInGlobalFilters: isInGlobalFilters, variant: variant, column: column, title: tooltipMessage },
        getKebabPlaceholderBefore(),
        React.createElement(StyledTableCellContentWithActionInner, { column: column, isInGlobalFilters: isInGlobalFilters, title: tooltip }, content),
        React.createElement(TableKebabActionMenu, Object.assign({}, idAttrs("kebab-", { prepend: true }), { actions: actionMenuRenderer, params: cellRendererParams, handleQuickFind: actionMenuHandleQuickFind, maxHeight: actionMenuMaxHeight, "aria-label": `Actions for row ${dataIndex}, column ${column.title}` })))) : (React.createElement(StyledTableCellContent, { isGroupParentCell: isGroupParentCell, isInGlobalFilters: isInGlobalFilters, variant: variant, column: column, title: tooltipMessage },
        isInGlobalFilters ? (React.createElement(StyledTableCellContentGlobalFilter, { column: column }, content)) : (content),
        getKebabPlaceholderAfter()))));
    const TableCellWithActions = withContextMenu(TableCellWithActionsTarget);
    if (hasContextMenu) {
        return (React.createElement(TableCellWithActions, { actions: contextMenuRenderer, handleQuickFind: actionColumnQuickFindHandler, targetCellProps: tableCellProps }, cell));
    }
    return React.createElement(StyledTableCell, Object.assign({}, tableCellProps), cell);
};
const TableCellWithActionsTarget = (_a) => {
    var { 
    // unused props
    hide, isVisible, tooltipPosition, show, update, actions, handleQuickFind, onKeyDown, tabIndex, forwardedRef, children, targetCellProps } = _a, rest = __rest(_a, ["hide", "isVisible", "tooltipPosition", "show", "update", "actions", "handleQuickFind", "onKeyDown", "tabIndex", "forwardedRef", "children", "targetCellProps"]);
    return (React.createElement(StyledTableCell, Object.assign({ ref: forwardedRef }, targetCellProps, rest, { onKeyDown: (e) => {
            // stops a context menu from opening, when a kebab menu is already opened,
            // and a user presses "Enter" to open a link in an action menu
            if (e.key !== "Enter") {
                onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
            }
        } }), children));
};
export default TableCell;
//# sourceMappingURL=TableCell.js.map